import React, { useState, useEffect } from 'react';
import { AlertTriangle, Info, CheckCircle, Save, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InfoBannerAdmin = () => {
  const [banner, setBanner] = useState({
    enabled: false,
    message: '',
    type: 'info'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    try {
      const response = await axios.get(`${API}/settings/info-banner`);
      setBanner(response.data);
    } catch (error) {
      console.error('Error fetching banner:', error);
      toast.error('Fehler beim Laden des Banners');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/info-banner`, banner);
      toast.success('Info-Banner aktualisiert');
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const getPreviewStyles = () => {
    switch (banner.type) {
      case 'warning':
        return 'bg-gradient-to-r from-amber-500 to-orange-500 text-white';
      case 'success':
        return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white';
      default:
        return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white';
    }
  };

  const getPreviewIcon = () => {
    switch (banner.type) {
      case 'warning':
        return <AlertTriangle className="h-5 w-5" />;
      case 'success':
        return <CheckCircle className="h-5 w-5" />;
      default:
        return <Info className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Info-Banner Verwaltung
          </CardTitle>
          <CardDescription>
            Verwalten Sie das Info-Banner, das oben auf der Website angezeigt wird
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Switch */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              {banner.enabled ? (
                <Eye className="h-5 w-5 text-green-500" />
              ) : (
                <EyeOff className="h-5 w-5 text-gray-400" />
              )}
              <div>
                <Label className="text-base font-medium">Banner anzeigen</Label>
                <p className="text-sm text-gray-500">
                  {banner.enabled ? 'Banner ist auf der Website sichtbar' : 'Banner ist ausgeblendet'}
                </p>
              </div>
            </div>
            <Switch
              checked={banner.enabled}
              onCheckedChange={(checked) => setBanner({ ...banner, enabled: checked })}
            />
          </div>

          {/* Banner Type */}
          <div className="space-y-2">
            <Label>Banner-Typ</Label>
            <Select
              value={banner.type}
              onValueChange={(value) => setBanner({ ...banner, type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Typ auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-500" />
                    Information (Blau)
                  </div>
                </SelectItem>
                <SelectItem value="warning">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    Warnung (Orange)
                  </div>
                </SelectItem>
                <SelectItem value="success">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Erfolg (Grün)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Message Text */}
          <div className="space-y-2">
            <Label>Nachricht</Label>
            <Textarea
              value={banner.message}
              onChange={(e) => setBanner({ ...banner, message: e.target.value })}
              placeholder="Geben Sie hier Ihre Nachricht ein..."
              rows={5}
              className="resize-none"
            />
            <p className="text-xs text-gray-500">
              Telefonnummern und E-Mail-Adressen werden automatisch klickbar gemacht
            </p>
          </div>

          {/* Preview */}
          {banner.message && (
            <div className="space-y-2">
              <Label>Vorschau</Label>
              <div className={`${getPreviewStyles()} p-4 rounded-lg`}>
                <div className="flex items-start gap-3">
                  {getPreviewIcon()}
                  <p className="text-sm leading-relaxed">{banner.message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600"
          >
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Speichern...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Banner speichern
              </>
            )}
          </Button>

          {/* Last Updated Info */}
          {banner.updatedAt && (
            <p className="text-xs text-gray-400 text-center">
              Zuletzt aktualisiert: {new Date(banner.updatedAt).toLocaleString('de-DE')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InfoBannerAdmin;
